import localforage from "localforage";

// 配置 localforage
localforage.config({
	name: "FOMO100",
	storeName: "tokens",
	description: "Token storage for FOMO100",
});

// Token 存储键名
const TOKEN_KEYS = {
	ACCESS_TOKEN: "access_token",
	REFRESH_TOKEN: "refresh_token",
	USER_INFO: "user_info",
} as const;

// Token 存储工具类
export class TokenStorage {
	/**
	 * 存储访问令牌
	 */
	static async setAccessToken(token: string): Promise<void> {
		try {
			await localforage.setItem(TOKEN_KEYS.ACCESS_TOKEN, token);
		} catch (error) {
			console.error("Failed to store access token:", error);
			throw error;
		}
	}

	/**
	 * 获取访问令牌
	 */
	static async getAccessToken(): Promise<string | null> {
		try {
			return await localforage.getItem<string>(TOKEN_KEYS.ACCESS_TOKEN);
		} catch (error) {
			console.error("Failed to get access token:", error);
			return null;
		}
	}

	/**
	 * 存储刷新令牌
	 */
	static async setRefreshToken(token: string): Promise<void> {
		try {
			await localforage.setItem(TOKEN_KEYS.REFRESH_TOKEN, token);
		} catch (error) {
			console.error("Failed to store refresh token:", error);
			throw error;
		}
	}

	/**
	 * 获取刷新令牌
	 */
	static async getRefreshToken(): Promise<string | null> {
		try {
			return await localforage.getItem<string>(TOKEN_KEYS.REFRESH_TOKEN);
		} catch (error) {
			console.error("Failed to get refresh token:", error);
			return null;
		}
	}

	/**
	 * 存储用户信息
	 */
	static async setUserInfo(userInfo: any): Promise<void> {
		try {
			await localforage.setItem(TOKEN_KEYS.USER_INFO, userInfo);
		} catch (error) {
			console.error("Failed to store user info:", error);
			throw error;
		}
	}

	/**
	 * 获取用户信息
	 */
	static async getUserInfo(): Promise<any | null> {
		try {
			return await localforage.getItem(TOKEN_KEYS.USER_INFO);
		} catch (error) {
			console.error("Failed to get user info:", error);
			return null;
		}
	}

	/**
	 * 同时存储访问令牌和刷新令牌
	 */
	static async setTokens(accessToken: string, refreshToken?: string): Promise<void> {
		try {
			await Promise.all([
				localforage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken),
				refreshToken ? localforage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken) : Promise.resolve(),
			]);
		} catch (error) {
			console.error("Failed to store tokens:", error);
			throw error;
		}
	}

	/**
	 * 清除所有存储的令牌和用户信息
	 */
	static async clearAll(): Promise<void> {
		try {
			await Promise.all([
				localforage.removeItem(TOKEN_KEYS.ACCESS_TOKEN),
				localforage.removeItem(TOKEN_KEYS.REFRESH_TOKEN),
				localforage.removeItem(TOKEN_KEYS.USER_INFO),
			]);
		} catch (error) {
			console.error("Failed to clear tokens:", error);
			throw error;
		}
	}

	/**
	 * 检查是否有有效的访问令牌
	 */
	static async hasValidToken(): Promise<boolean> {
		try {
			const token = await localforage.getItem<string>(TOKEN_KEYS.ACCESS_TOKEN);
			return !!token;
		} catch (error) {
			console.error("Failed to check token validity:", error);
			return false;
		}
	}

	/**
	 * 获取所有令牌
	 */
	static async getAllTokens(): Promise<{
		accessToken: string | null;
		refreshToken: string | null;
		userInfo: any | null;
	}> {
		try {
			const [accessToken, refreshToken, userInfo] = await Promise.all([
				localforage.getItem<string>(TOKEN_KEYS.ACCESS_TOKEN),
				localforage.getItem<string>(TOKEN_KEYS.REFRESH_TOKEN),
				localforage.getItem(TOKEN_KEYS.USER_INFO),
			]);

			return {
				accessToken,
				refreshToken,
				userInfo,
			};
		} catch (error) {
			console.error("Failed to get all tokens:", error);
			return {
				accessToken: null,
				refreshToken: null,
				userInfo: null,
			};
		}
	}
}

export default TokenStorage;
